<template>
  <div class="rule-row">
    <el-select
      v-model="localRule.field"
      placeholder="Select attribute"
      class="rule-field-select"
      :class="[blurMode ? 'blurelement' : '']"
      @change="onFieldChange"
    >
      <el-option
        v-for="attr in availableAttributes"
        :key="attr.field"
        :label="attr.label"
        :value="attr.field"
      ></el-option>
    </el-select>

    <el-select
      v-model="localRule.operator"
      placeholder="Select operator"
      class="rule-operator-select"
      :class="[blurMode ? 'blurelement' : '']"
      @change="onOperatorChange"
    >
      <el-option
        v-for="op in availableOperators"
        :key="op.value"
        :label="op.label"
        :value="op.value"
      ></el-option>
    </el-select>

    <div class="rule-value-container">
      <!-- Single value input -->
      <el-input-number
        v-if="currentOperator && currentOperator.valueType === 'single' && attributeConfig && attributeConfig.type === 'number'"
        v-model="localRule.value"
        :min="attributeConfig ? attributeConfig.min : undefined"
        :max="attributeConfig ? attributeConfig.max : undefined"
        :precision="0"
        class="rule-value-input"
        @change="onValueChange"
      ></el-input-number>

      <!-- Range input (BETWEEN) -->
      <template v-else-if="currentOperator && currentOperator.valueType === 'range'">
        <el-input-number
          v-model="localRule.value[0]"
          :min="attributeConfig ? attributeConfig.min : undefined"
          :max="attributeConfig ? attributeConfig.max : undefined"
          :precision="0"
          class="rule-value-input rule-value-range"
          placeholder="Min"
          @change="onValueChange"
        ></el-input-number>
        <span class="rule-value-separator">and</span>
        <el-input-number
          v-model="localRule.value[1]"
          :min="attributeConfig ? attributeConfig.min : undefined"
          :max="attributeConfig ? attributeConfig.max : undefined"
          :precision="0"
          class="rule-value-input rule-value-range"
          placeholder="Max"
          @change="onValueChange"
        ></el-input-number>
      </template>

      <!-- Single select -->
      <el-select
        v-else-if="currentOperator && currentOperator.valueType === 'single' && attributeConfig && attributeConfig.type === 'select'"
        v-model="localRule.value"
        filterable
        placeholder="Select value"
        class="rule-value-select"
        :class="[blurMode ? 'blurelement' : '']"
        @change="onValueChange"
      >
        <el-option
          v-for="opt in selectOptions"
          :key="opt.value"
          :label="opt.label"
          :value="opt.value"
        ></el-option>
      </el-select>

      <!-- Multiple select -->
      <el-select
        v-else-if="currentOperator && currentOperator.valueType === 'multiple' && attributeConfig && attributeConfig.type === 'select'"
        v-model="localRule.value"
        multiple
        filterable
        placeholder="Select values"
        class="rule-value-select"
        :class="[blurMode ? 'blurelement' : '']"
        @change="onValueChange"
      >
        <el-option
          v-for="opt in selectOptions"
          :key="opt.value"
          :label="opt.label"
          :value="opt.value"
        ></el-option>
      </el-select>

      <!-- Async select (single) -->
      <el-autocomplete
        v-else-if="currentOperator && currentOperator.valueType === 'single' && attributeConfig && attributeConfig.type === 'async-select'"
        v-model="asyncSelectDisplayValue"
        :fetch-suggestions="queryAsyncOptions"
        placeholder="Type to search"
        class="rule-value-select"
        :class="[blurMode ? 'blurelement' : '']"
        @select="onAsyncSelectChange"
      >
        <template slot-scope="{ item }">
          <div class="value">{{ item.label }}</div>
        </template>
      </el-autocomplete>

      <!-- Async select (multiple) - using cascader-like multi-select -->
      <el-select
        v-else-if="currentOperator && currentOperator.valueType === 'multiple' && attributeConfig && attributeConfig.type === 'async-select'"
        v-model="localRule.value"
        multiple
        filterable
        allow-create
        default-first-option
        placeholder="Type to search"
        class="rule-value-select"
        :class="[blurMode ? 'blurelement' : '']"
        @change="onValueChange"
      >
        <el-option
          v-for="opt in asyncSelectOptions"
          :key="opt.value"
          :label="opt.label"
          :value="opt.value"
        ></el-option>
      </el-select>
    </div>

    <el-button
      type="text"
      icon="el-icon-delete"
      class="rule-delete-btn"
      @click="$emit('remove')"
    ></el-button>
  </div>
</template>

<script>
import { getAllAttributes, getAttributeConfig } from './attribute-config';

export default {
  name: 'RuleRow',
  props: {
    rule: {
      type: Object,
      required: true,
    },
    availableAttributes: {
      type: Array,
      default: () => getAllAttributes(),
    },
    blurMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localRule: { ...this.rule },
      selectOptions: [],
      asyncSelectOptions: [],
      loadingOptions: false,
    };
  },
  computed: {
    attributeConfig() {
      if (!this.localRule.field) return undefined;
      return getAttributeConfig(this.localRule.field);
    },
    availableOperators() {
      return (this.attributeConfig && this.attributeConfig.operators) || [];
    },
    currentOperator() {
      if (!this.localRule.operator) return undefined;
      return this.availableOperators.find((op) => op.value === this.localRule.operator);
    },
    asyncSelectDisplayValue: {
      get() {
        // Convert value back to object for display
        if (!this.localRule.value || !this.asyncSelectOptions.length) {
          return '';
        }
        const selectedOption = this.asyncSelectOptions.find(
          (opt) => opt.value === this.localRule.value
        );
        return selectedOption ? selectedOption.label : '';
      },
      set(value) {
        // When user types, find matching option or keep as string
        if (typeof value === 'string') {
          const matchedOption = this.asyncSelectOptions.find(
            (opt) => opt.label === value
          );
          if (matchedOption) {
            this.localRule.value = matchedOption.value;
            this.emitChange();
          }
        }
      },
    },
  },
  watch: {
    rule: {
      handler(newRule) {
        this.localRule = { ...newRule };
      },
      deep: true,
    },
    asyncSelectOptions() {
      if (
        !this.attributeConfig ||
        this.attributeConfig.type !== 'async-select' ||
        !this.localRule.value
      ) {
        return;
      }
      this.ensureAsyncSelections();
    },
  },
  mounted() {
    this.loadOptions();
  },
  methods: {
    onFieldChange() {
      // Reset operator and value when field changes
      if (this.availableOperators.length > 0) {
        this.localRule.operator = this.availableOperators[0].value;
      }
      this.localRule.value = undefined;
      this.loadOptions();
      this.emitChange();
    },
    onOperatorChange() {
      // Reset value when operator changes
      const op = this.currentOperator;
      if (op && op.valueType === 'range') {
        this.localRule.value = [null, null];
      } else if (op && op.valueType === 'multiple') {
        this.localRule.value = [];
      } else {
        this.localRule.value = undefined;
      }
      this.emitChange();
    },
    onValueChange() {
      this.emitChange();
    },
    emitChange() {
      // Add window property for metrics that require it
      const rule = { ...this.localRule };
      if (this.attributeConfig && this.attributeConfig.requiresWindow) {
        rule.window = 'most_recent';
      }
      this.$emit('update', rule);
    },
    async loadOptions() {
      if (!this.attributeConfig) return;

      // Load select options
      if (this.attributeConfig.type === 'select' || this.attributeConfig.type === 'async-select') {
        this.loadingOptions = true;
        try {
          const options = await this.getOptionsForAttribute(this.attributeConfig);
          if (this.attributeConfig.type === 'select') {
            this.selectOptions = options;
          } else {
            this.asyncSelectOptions = options;
            this.ensureAsyncSelections();
          }
        } catch (error) {
          console.error('Error loading options:', error);
        } finally {
          this.loadingOptions = false;
        }
      }
    },
    async getOptionsForAttribute(config) {
      if (config.field === 'team') {
        // TODO (Backend): Load from store or API
        // For now, return empty - will be populated from store in parent
        const membersByTeam = this.$store.getters['membersCohort/membersByTeam'];
        const teamMaster = this.$store.getters['powerups/teamMaster'];
        return membersByTeam
          ? Object.keys(membersByTeam).map((teamId) => {
              const team = teamMaster && teamMaster[teamId];
              return {
                label: (team && team.name) || teamId,
                value: teamId,
              };
            })
          : [];
      } else if (config.field === 'house') {
        // TODO (Backend): Load houses from store
        const houseMap = {
          '-LWPrZbcyHAVHIFUsWLd': 'Corvus',
          '-LWOr-XsbZM2digYl1QL': 'Delphinus',
          '-LWPrOElCYViBd6j6qP-': 'Leonos',
        };
        return Object.entries(houseMap).map(([id, name]) => ({
          label: name,
          value: id,
        }));
      } else if (config.field === 'university' && config.getOptions) {
        // TODO (Backend): Implement async loader for university sessions
        return await config.getOptions() || [];
      }
      return config.getOptions ? await config.getOptions() : [];
    },
    async queryAsyncOptions(queryString, cb) {
      if (!queryString) {
        cb(this.asyncSelectOptions);
        return;
      }
      const results = this.asyncSelectOptions.filter((opt) =>
        opt.label.toLowerCase().includes(queryString.toLowerCase())
      );
      cb(results);
    },
    onAsyncSelectChange(item) {
      // When user selects an item, update localRule.value with the value property
      if (item && item.value !== undefined) {
        this.localRule.value = item.value;
        this.emitChange();
      }
    },
    ensureAsyncSelections() {
      if (!this.localRule.value) {
        return;
      }
      const values = Array.isArray(this.localRule.value)
        ? this.localRule.value
        : [this.localRule.value];
      const mergedOptions = [...this.asyncSelectOptions];
      let hasChanges = false;
      values.forEach((value) => {
        const exists = mergedOptions.some((opt) => opt.value === value);
        if (!exists) {
          mergedOptions.push({
            value,
            label: this.getLabelForValue(value),
          });
          hasChanges = true;
        }
      });
      if (hasChanges) {
        this.asyncSelectOptions = mergedOptions;
      }
    },
    getLabelForValue(value) {
      // Attempt to find label from existing members if available; fallback to value
      const option = this.asyncSelectOptions.find((opt) => opt.value === value);
      if (option) {
        return option.label;
      }
      const membersall = this.$store.getters['membersCohort/membersall'] || {};
      const member = membersall[value];
      if (member && member.display_name) {
        return member.display_name;
      }
      return value;
    },
  },
};
</script>

<style lang="scss" scoped>
.rule-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  padding: 10px 12px;
  background-color: #ffffff;
  border: 1px solid #e4e4e4;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #32c2d5;
    box-shadow: 0 2px 8px rgba(50, 194, 213, 0.1);
  }

  .rule-field-select {
    flex: 0 0 200px;
  }

  .rule-operator-select {
    flex: 0 0 140px;
  }

  .rule-value-container {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;

    .rule-value-input {
      width: 140px;
      
      ::v-deep .el-input__inner {
        padding-left: 40px;
        padding-right: 40px;
      }
    }

    .rule-value-range {
      width: 110px;
      
      ::v-deep .el-input__inner {
        padding-left: 40px;
        padding-right: 40px;
      }
    }

    .rule-value-separator {
      font-size: 14px;
      color: #606266;
      white-space: nowrap;
      font-weight: 500;
    }

    .rule-value-select {
      flex: 1;
      min-width: 180px;
    }

    .rule-value-loading {
      color: #32c2d5;
      font-size: 16px;
      margin-left: 8px;
    }
  }

  .rule-delete-btn {
    flex: 0 0 auto;
    color: #f56c6c;
    padding: 4px;
    font-size: 18px;
    margin-left: 8px;

    &:hover {
      color: #f56c6c;
      background-color: #fef0f0;
      border-radius: 4px;
    }
  }
}
</style>

